import type { RowDragEvent } from "ag-grid-community";
import { ChevronRightIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Disclosure, DisclosurePanel } from "react-aria-components";

import { type RouterOutputs, trpc } from "@blis/trpc/react";

import { Link } from "@/components/Link";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Modal } from "@/components/ui/modal";
import { Tab, Tabs, TabsList } from "@/components/ui/tabs";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { useAdminGridOptions } from "../../utils/gridState";
import { AdminGrid } from "../AdminGrid";
import { TabLayout } from "../TabLayout";
import { ResultSpecDetailForm } from "./ResultSpecDetailForm";

type ResultSpec = RouterOutputs["admin"]["testResultSpec"]["list"][0];

export function ResultSpecsTable({ testSpec }: { testSpec: { id: number } }) {
  const [orderBy, setOrderBy] = useState<"display" | "report">("display");
  const { data } = trpc.admin.testResultSpec.list.useQuery({
    testSpecId: testSpec.id,
  });
  const resultSpecs = useMemo(() => {
    return [...(data || [])].sort((a, b) => {
      if (orderBy === "report") {
        return (a.reportDisplayOrder ?? a.id) - (b.reportDisplayOrder ?? b.id);
      }
      return (a.displayOrder ?? a.id) - (b.displayOrder ?? b.id);
    });
  }, [data, orderBy]);

  const enabledResultSpecs = useMemo(
    () => resultSpecs.filter((rs) => rs.enabled !== false),
    [resultSpecs],
  );

  const disabledResultSpecs = useMemo(
    () => resultSpecs.filter((rs) => rs.enabled === false),
    [resultSpecs],
  );

  const { mutate } = trpc.admin.testResultSpec.updateOrder.useMutation({
    async onSuccess() {
      toast.success(`Results reordered for: ${orderBy}`);
    },
    onError(err) {
      toast.error("Error reordering results", {
        description: err.message,
      });
    },
  });

  const onRowDragEnd = useCallback(
    (e: RowDragEvent<ResultSpec>) => {
      const newRowsDisplay: {
        testResultSpecId: number;
        displayOrder: number;
      }[] = [];
      const newRowsReport: {
        testResultSpecId: number;
        reportDisplayOrder: number;
      }[] = [];

      e.api.forEachNode((node, idx) => {
        if (node.data?.id) {
          if (orderBy === "display") {
            newRowsDisplay.push({
              testResultSpecId: node.data.id,
              displayOrder: idx,
            });
          } else {
            newRowsReport.push({
              testResultSpecId: node.data.id,
              reportDisplayOrder: idx,
            });
          }
        }
      });
      mutate({
        testSpecId: testSpec.id,
        items: orderBy === "display" ? newRowsDisplay : newRowsReport,
      });
    },
    [mutate, orderBy, testSpec.id],
  );

  const enabledOptions = useAdminGridOptions(
    enabledResultSpecs,
    [
      {
        headerName: "Name",
        field: "name",
        rowDrag: true,
        cellRenderer: (info) => (
          <Link
            to="/admin/tests/$id/edit/results/$specId"
            params={{ id: testSpec.id, specId: info.data.id }}
          >
            {info.value}
          </Link>
        ),
        cellRendererParams: (info) => ({
          name: info.data.name,
          id: info.data.id,
        }),
      },
      { field: "componentType" },
      {
        headerName: "Type",
        field: "type",
      },
      {
        headerName: "Units",
        field: "units",
      },
      {
        headerName: "Is conditional",
        field: "isConditional",
      },
    ],
    {
      sideBar: undefined,
      getRowId: (r) => r?.data.id.toString(),
      onRowDragEnd,
    },
  );

  return (
    <TabLayout
      description={
        <div className="flex flex-col">
          <div className="flex items-center gap-2 mt-3">
            <Text muted>Order by:</Text>
            <Tabs
              variant="segment"
              selectedKey={orderBy}
              onSelectionChange={(val) => setOrderBy(val as "display")}
            >
              <TabsList>
                <Tab id="display">Display</Tab>
                <Tab id="report">Report</Tab>
              </TabsList>
            </Tabs>
          </div>
        </div>
      }
      actions={<AddResultSpecDialog test={testSpec} />}
    >
      <div className="flex h-96">
        <AdminGrid {...enabledOptions.options} />
      </div>

      {disabledResultSpecs.length > 0 && (
        <DisabledResultSpecs specs={disabledResultSpecs} />
      )}
    </TabLayout>
  );
}

function AddResultSpecDialog({ test }: { test: { id: number } }) {
  const [open, setOpen] = useState(false);
  const [fetching, setFetching] = useState(false);
  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button variant="secondary">Add result spec</Button>
      <Modal>
        <Dialog>
          <DialogHeader>
            <DialogTitle>Add test result spec</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <ResultSpecDetailForm
              formId="add-result-spec-form"
              hideFooter
              test={test}
              onComplete={() => setOpen(false)}
              onFetching={setFetching}
            />
          </DialogBody>
          <DialogFooter>
            <Button
              type="submit"
              form="add-result-spec-form"
              isPending={fetching}
            >
              Submit
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}

function DisabledResultSpecs({ specs }: { specs: ResultSpec[] }) {
  const disabledOptions = useAdminGridOptions(
    specs,
    [
      { field: "name" },
      { field: "type" },
      { field: "units" },
      { field: "isConditional" },
    ],
    { sideBar: undefined },
  );

  return (
    <Disclosure className="group">
      <Button slot="trigger" variant="ghost" className="mt-6">
        <Text size="2" muted className="cursor-pointer flex items-center gap-2">
          <ChevronRightIcon className="size-4 -mr-1 transition-all group-data-[expanded=true]:rotate-90" />
          View disabled results ({specs.length})
        </Text>
      </Button>
      <DisclosurePanel>
        <div className="mt-2 flex flex-col h-96">
          <AdminGrid {...disabledOptions.options} />
        </div>
      </DisclosurePanel>
    </Disclosure>
  );
}
